import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { ApplicationHttpClient } from 'app/components/shared/http/application-http-client';
import { Routes } from '../../config/routes';
import { LegFiJwtService } from '../auth/legfi-jwt.service';
import { JwtLegFiClaims } from '../auth/jwt-legfi-claims.model';

export type GableSkillSource = 'upload' | 'manual' | 'gable' | 'library';
export type GableSkillSizeBand = 'under_100' | '100_500' | 'over_500';
export type GableSkillReachMode = 'all' | 'selected';
export type GableSkillExceptionType = 'always' | 'never';

export interface GableSkillReachFilters {
    states: string[];
    sizeBands: GableSkillSizeBand[];
    customRange: { min: number; max: number } | null;
}

export interface GableSkillReachException {
    organizationId: number;
    name: string;
    type: GableSkillExceptionType;
}

export interface GableSkillReach {
    mode: GableSkillReachMode;
    filters: GableSkillReachFilters;
    exceptions: GableSkillReachException[];
}

/** List-row shape — no body, cheap to render in the table. */
export interface GableSkillRow {
    id: number;
    name: string;
    description: string | null;
    enabled: boolean;
    source: GableSkillSource;
    authorName: string;
    updatedAt: string | null;
    reachSummary: string;
}

export interface GableSkillFull extends GableSkillRow {
    body: string;
    createdAt: string | null;
    isEdited: boolean;
    reach: GableSkillReach;
}

/** Body for POST/PATCH. PATCH fields are all optional. */
export interface GableSkillInput {
    name?: string;
    description?: string | null;
    body?: string;
    enabled?: boolean;
    source?: GableSkillSource;
    reachMode?: GableSkillReachMode;
    reachFilters?: GableSkillReachFilters;
    reachExceptions?: Array<{ organizationId: number; type: GableSkillExceptionType }>;
}

export interface GableSkillStarter {
    key: string;
    name: string;
    description: string;
    body: string;
}

export interface GableSkillReachPreview {
    count: number;
    total: number;
    examples: string[];
}

export interface GableSkillOrgLookupResult {
    id: number;
    name: string;
    matchesFilters: boolean;
}

export interface GableSkillDraft {
    name: string;
    description: string;
    body: string;
    notes: string;
}

// Thin client for the Gable skills admin API — super-only routes.
@Injectable({ providedIn: 'root' })
export class GableSkillsService {
    constructor(private readonly _http: ApplicationHttpClient) {}

    list(): Observable<{ skills: GableSkillRow[] }> {
        const jwt = this._requireJwt();
        return this._http.get<{ skills: GableSkillRow[] }>(this._url(jwt));
    }

    show(id: number): Observable<{ skill: GableSkillFull }> {
        const jwt = this._requireJwt();
        return this._http.get<{ skill: GableSkillFull }>(this._itemUrl(jwt, id));
    }

    create(body: GableSkillInput): Observable<{ skill: GableSkillFull }> {
        const jwt = this._requireJwt();
        return this._http.post<{ skill: GableSkillFull }>(
            this._url(jwt),
            this._http.serializeObject(body as { [k: string]: any }),
        );
    }

    update(id: number, body: GableSkillInput): Observable<{ skill: GableSkillFull }> {
        const jwt = this._requireJwt();
        return this._http.patch<{ skill: GableSkillFull }>(
            this._itemUrl(jwt, id),
            this._http.serializeObject(body as { [k: string]: any }),
        );
    }

    remove(id: number): Observable<{ success: boolean }> {
        const jwt = this._requireJwt();
        return this._http.delete<{ success: boolean }>(this._itemUrl(jwt, id));
    }

    starters(): Observable<{ starters: GableSkillStarter[] }> {
        const jwt = this._requireJwt();
        const url = Routes.MakeLegFiCoreUrl(Routes.GableModule.SkillsStarters(jwt.orgId));
        return this._http.get<{ starters: GableSkillStarter[] }>(url);
    }

    reachPreview(body: {
        filters: GableSkillReachFilters;
        exceptions?: Array<{ organizationId: number; type: GableSkillExceptionType }>;
    }): Observable<GableSkillReachPreview> {
        const jwt = this._requireJwt();
        const url = Routes.MakeLegFiCoreUrl(Routes.GableModule.SkillsReachPreview(jwt.orgId));
        return this._http.post<GableSkillReachPreview>(
            url,
            this._http.serializeObject(body as { [k: string]: any }),
        );
    }

    orgLookup(body: { q: string; filters: GableSkillReachFilters }): Observable<{ organizations: GableSkillOrgLookupResult[] }> {
        const jwt = this._requireJwt();
        const url = Routes.MakeLegFiCoreUrl(Routes.GableModule.SkillsOrgLookup(jwt.orgId));
        return this._http.post<{ organizations: GableSkillOrgLookupResult[] }>(
            url,
            this._http.serializeObject(body as { [k: string]: any }),
        );
    }

    /** "Write it with Gable" — drafts skill markdown from a plain-words brief. */
    draft(brief: string): Observable<GableSkillDraft> {
        const jwt = this._requireJwt();
        const url = Routes.MakeLegFiCoreUrl(Routes.GableModule.SkillsDraft(jwt.orgId));
        return this._http.post<GableSkillDraft>(
            url,
            this._http.serializeObject({ brief } as { [k: string]: any }),
        );
    }

    private _url(jwt: JwtLegFiClaims): string {
        return Routes.MakeLegFiCoreUrl(Routes.GableModule.Skills(jwt.orgId));
    }

    private _itemUrl(jwt: JwtLegFiClaims, id: number): string {
        return Routes.MakeLegFiCoreUrl(Routes.GableModule.Skill(jwt.orgId, id));
    }

    private _requireJwt(): JwtLegFiClaims {
        const jwt = LegFiJwtService.read();
        if (jwt === null) {
            throw new Error('No JWT — cannot call skills endpoint');
        }
        return jwt;
    }
}
